'use client'

import CreateDialog from '@/shared/components/categories/CreateDialog'
import DeleteDialog from '@/shared/components/categories/DeleteDialog'
import EditDialog from '@/shared/components/categories/EditDialog'
import MenuDropdown from '@/shared/components/categories/MenuDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useAdd } from '@/shared/hooks/categories/masterTypes/useAdd'
import { useDelete, useDeleteMulti } from '@/shared/hooks/categories/masterTypes/useDelete'
import useGetMasterTypeCatalog from '@/shared/hooks/categories/masterTypes/useGetMasterTypeDetail'
import { useUpdate } from '@/shared/hooks/categories/masterTypes/useUpdate'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useRegions } from '@/shared/hooks/useRegions'
import TabLayout, { ActionButton } from '@/shared/layouts/TabLayout'
import { createFileRoute, useLoaderData, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, DataTableSorting } from '@workspace/ui/components/DataTable'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { toast } from '@workspace/ui/components/Sonner'
import dayjs from 'dayjs'
import { PlusIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCan } from '@/shared/lib/authorization'

interface DocumentType {
    id: string
    code: string
    name: string
    description: string
    isActive: boolean
    createdAt: string
}

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/categories/_pathlessLayout/$id/')({
    validateSearch: searchSchema,
    component: MasterTypesPage,
})

function MasterTypesPage() {
    const { id: masterTypeCode } = Route.useParams()
    const { links } = useLoaderData({ from: '/categories/_pathlessLayout' })
    const { t } = useTranslation()
    const deleteSingle = useDelete()
    const deleteMulti = useDeleteMulti()

    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const searchQuery = searchParams.q || ''
    const page = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : null

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState({})

    const [isCreateOpen, setCreateOpen] = useState(false)
    const [editOpen, setEditOpen] = useState<DocumentType | boolean>(false)
    const [deleteCatalog, setDeleteCatalog] = useState<string | 'multiple' | boolean>(false)

    // Debounce search query
    const debouncedSearch = useDebounce(searchQuery, 300)

    const { data: regions } = useRegions({
        enabled: masterTypeCode === 'functional_department',
    })

    const { masterTypeId, label } = useMemo(() => {
        const masterType = links.find(link => link.code === masterTypeCode)
        return {
            masterTypeId: masterType?.masterTypeId,
            label: masterType?.label,
        }
    }, [masterTypeCode, links])

    const masterTypeLabel = label?.toLocaleLowerCase() || ''
    const addCatalog = useAdd({ name: masterTypeLabel })
    const update = useUpdate(masterTypeLabel)

    const { data: masterTypeData, isFetching } = useGetMasterTypeCatalog({
        masterTypeId,
        page,
        pageSize,
        q: debouncedSearch,
        sorting,
    })

    const totalPages = Math.ceil((masterTypeData?.meta?.itemCount || 0) / pageSize)
    const selectedCount = Object.keys(rowSelection).length
    const selectedIds = Object.keys(rowSelection)

    const handleCreateCatalog = async (values: { name: string; code: string; description?: string }) => {
        await addCatalog.mutateAsync({
            ...values,
            masterTypeId,
            isActive: true,
            isSystem: false,
        })
        setCreateOpen(false)
    }

    const handleDeleteMultiConfirm = async () => {
        if (selectedCount === 0) return
        const currentItemCount = masterTypeData?.data.length
        const willPageBeEmpty = currentItemCount === selectedCount && page > 1

        await deleteMulti.mutateAsync({ ids: selectedIds })
        toast.success({
            title: 'Xóa loại tài liệu thành công',
            description: `Đã xóa ${selectedCount} loại tài liệu.`,
        })
        setRowSelection({})
        if (willPageBeEmpty) {
            setPage(page - 1)
        }
    }

    const handleDelete = async () => {
        if (!deleteCatalog) return
        const currentItemCount = masterTypeData?.data.length
        const willPageBeEmpty = currentItemCount === 1 && page > 1

        await deleteSingle.mutateAsync({
            id: deleteCatalog as string,
        })
        setDeleteCatalog(false)
        setRowSelection({})

        if (willPageBeEmpty) {
            setPage(page - 1)
        }
    }

    const handleUpdateCatalog = async (values: any) => {
        const record = masterTypeData?.data?.find(catalog => catalog.id === values.id)
        await update.mutateAsync({
            id: values.id,
            data: {
                ...record,
                ...values,
                description: values.description || '',
            },
        })
    }

    const handleEditClick = useCallback((row: DocumentType) => {
        setEditOpen(row)
    }, [])

    const handleDeleteClick = useCallback((id: string) => {
        setDeleteCatalog(id)
    }, [])

    const showActionColumn = useCan('operate', 'library')

    const columns = useMemo<ColumnDef<DocumentType>[]>(
        () => [
            ...(showActionColumn
                ? [
                      {
                          id: 'action',
                          accessorKey: 'action',
                          header: '',
                          size: 50,
                          cell: ({ row }: any) => (
                              <MenuDropdown
                                  id={row.original.id}
                                  onEdit={() => handleEditClick(row.original)}
                                  onDelete={handleDeleteClick}
                              />
                          ),
                          enableSorting: false,
                      },
                  ]
                : []),
            {
                id: 'code',
                accessorKey: 'code',
                header: t('categories.createMasterType.code', {
                    name: masterTypeLabel,
                }),
                size: 260,
                enableSorting: true,
                meta: { className: 'px-2 py-2 text-sm text-secondary-foreground' },
                cell: ({ getValue }) => <span className="text-sm text-foreground">{getValue() as string}</span>,
            },
            {
                id: 'name',
                accessorKey: 'name',
                size: 260,
                header: t('categories.table.masterType', {
                    name: masterTypeLabel?.charAt(0).toUpperCase() + masterTypeLabel?.slice(1),
                }),
                enableSorting: true,
                meta: { className: 'px-2 py-2 text-sm text-secondary-foreground' },
                cell: ({ row, getValue }) => (
                    <span
                        className="text-sm text-foreground cursor-pointer"
                        onClick={() => handleEditClick(row.original)}
                    >
                        {getValue() as string}
                    </span>
                ),
            },
            ...(masterTypeCode === 'functional_department'
                ? [
                      {
                          id: 'region.name',
                          accessorKey: 'region.name',
                          header: t('tabs.regions'),
                          enableSorting: true,
                          size: 200,
                          meta: { className: 'px-2 py-2 text-sm text-secondary-foreground' },
                          cell: ({ row }: any) => {
                              const regionName = row.original?.region?.name
                              return <span className="text-sm text-foreground">{regionName || '-'}</span>
                          },
                      },
                  ]
                : []),
            {
                id: 'description',
                accessorKey: 'description',
                header: t('categories.table.description'),
                size: 480,
                enableSorting: false,
                meta: { className: 'px-2 py-2 text-sm text-secondary-foreground' },
                cell: ({ getValue }) => <span className="text-sm text-foreground">{getValue() as string}</span>,
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: t('categories.table.createdDate'),
                size: 140,
                enableSorting: true,
                meta: { className: 'px-2 py-2 text-sm text-secondary-foreground' },
                cell: ({ getValue }) => {
                    const value = getValue() as string | undefined
                    return <span className="text-sm">{value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'}</span>
                },
            },
        ],
        [t, handleEditClick, handleDeleteClick, masterTypeLabel, page, masterTypeCode],
    )

    const actions: ActionButton[] = [
        {
            text: t('categories.createMasterTypeButton', { name: masterTypeLabel }),
            icon: PlusIcon,
            onClick: () => setCreateOpen(true),
            variant: 'default',
        },
    ]

    return (
        <>
            <TabLayout
                className="h-[calc(100vh-150px)] min-h-[500px] overflow-hidden"
                title={t('categories.title')}
                linksTo={links}
                actions={actions}
            >
                <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                    <div className="shrink-0 flex gap-4">
                        <BsSearchField
                            autoTrim
                            className="h-10 text-sm bg-background rounded-lg w-80"
                            value={searchQuery}
                            placeholder={t('categories.searchPlaceholder')}
                            onChange={value => setSearchQuery(value)}
                        />
                    </div>
                    <DataTable
                        isLoading={isFetching}
                        data={masterTypeData?.data || []}
                        columns={columns}
                        enableRowSelection={true}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        enableSorting={true}
                        sorting={sorting}
                        setSorting={setSorting}
                        emptyString={t('common.emptyState')}
                        containerClassName="flex-1 overflow-y-auto min-h-0"
                    />
                    <div className="shrink-0">
                        <TableDataFooter
                            selectedCount={selectedCount}
                            onDelete={handleDeleteMultiConfirm}
                            deleteDisabled={selectedCount === 0 || deleteMulti.isPending}
                            isLoading={deleteMulti.isPending}
                            totalCount={masterTypeData?.meta?.itemCount || 0}
                            currentPage={page}
                            pageSize={pageSize}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={value => setPageSize(value || 10)}
                        />
                    </div>
                </div>
            </TabLayout>
            <CreateDialog
                isOpen={isCreateOpen}
                onOpenChange={setCreateOpen}
                onConfirm={handleCreateCatalog}
                type={masterTypeCode === 'functional_department' ? 'functional_department' : 'document'}
                listRegion={regions}
                masterTypeLabel={masterTypeLabel}
            />
            <DeleteDialog
                isOpen={!!deleteCatalog}
                onOpenChange={setDeleteCatalog}
                onConfirm={handleDelete}
                isLoading={deleteSingle.isPending}
            />

            <EditDialog
                isOpen={!!editOpen}
                onOpenChange={setEditOpen}
                onConfirm={handleUpdateCatalog}
                type={masterTypeCode === 'functional_department' ? 'functional_department' : 'document'}
                value={editOpen}
                isLoading={update.isPending}
                listRegion={regions}
                masterTypeLabel={masterTypeLabel}
            />
        </>
    )
}
